import { useMemo, useState } from "react";
import { Modal, Button } from "@material-ui/core";
import clsx from "clsx";
import { ModalHeader } from "./ModalHeader";
import CustomAutoComplete from "./CustomAutoComplete";
import RaceDataModal from "./RaceDataModal";
import PoldFields from "./PoldFields";

/**
 * sections: {
 *   drugTypes?: true,
 *   race?: { ticketId },
 *   axon?: true,
 *   pold?: true,
 *   classification?: true,
 *   incidentFlags?: true
 * }
 *
 * This modal does NOT automatically set status. It just helps the user fill fields.
 * After closing, the user can set status again (your preferred flow).
 */
export default function ReviewFixModal({
  open,
  onClose,
  classes,
  nightMode,
  title = "Missing Required Information",
  sections = {},
  errors = [],
  // For drug types
  drugTypes = [],
  classifications = [],
  incidentFlags = [],
  incident,
  user,
  handleReviewFixUpdate,
  setPausePolling,
  // For race data
  reviewStatuses,
  handleReadyForApproval, // optional: if you want the "OK" to re-trigger the ready flow
  revalidateCallback, // likely redundant with the above function but it passes the RFA arguments directly
  raceTicket = null,
  // Flags / future sections
  showAxon,
  showPold,
  ...props
}) {
  const [raceModalOpen, setRaceModalOpen] = useState(false);
  const raceTicketToEdit = useMemo(() => {
    if (raceTicket) return raceTicket;

    const sectionTicketId = sections?.race?.ticketId;
    if (sectionTicketId) {
      const sectionMatch = incident?.incidentTickets?.find(ticket =>
        [ticket?.id, ticket?.uuid].some(
          value => value != null && String(value) === String(sectionTicketId)
        )
      );
      if (sectionMatch) return sectionMatch;
    }

    return (
      incident?.incidentTickets?.find(ticket => !!ticket?.operator?.id) ||
      incident?.incidentTickets?.find(ticket => !!ticket?.perceivedRace?.id) ||
      incident?.incidentTickets?.[0] ||
      null
    );
  }, [incident?.incidentTickets, raceTicket, sections?.race?.ticketId]);

  const hasAnySection = useMemo(
    () =>
      !!sections?.drugTypes ||
      !!sections?.race ||
      !!sections?.axon ||
      !!sections?.pold ||
      !!sections?.classification ||
      !!sections?.incidentFlags,
    [sections],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={classes?.modal}
      style={{ width: "fit-content", margin: "auto", padding: "10px" }}
      data-cy="review-fix-modal"
      onKeyDown={e => {
        if (e.key === "Escape") onClose();
      }}
      disablePortal={true}
    >
      <div
        id="review-fix-modal-wrapper"
        style={{
          height: "fit-content",
          width: "fit-content",
          borderRadius: "5px",
        }}
        className={classes?.tabRoot}
      >
        <ModalHeader
          headerName={title}
          setModalOpen={onClose}
          nightMode={nightMode}
          helpIconObjectName={""}
        />

        {!hasAnySection && errors.length === 0 && (
          <div style={{ padding: 16 }}>All set—no missing fields detected.</div>
        )}
        <div id="review-fix-modal-content" style={{ padding: "10px" }}>
          {/* Add a list of the errors here at the top */}
          {errors.length > 0 && (
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                border: "1px solid red",
                borderRadius: "5px",
                backgroundColor: "#ffe6e6",
              }}
            >
              <strong>The following issues need to be addressed:</strong>
              <ul style={{ marginTop: 8 }}>
                {errors.map((error, index) => (
                  <li key={`review-fix-error-${index}`}>{error}</li>
                ))}
              </ul>
            </div>
          )}
          {/* Drug Types section */}
          {sections?.drugTypes && (
            <div style={{ padding: "0 16px 16px 16px" }}>
              <CustomAutoComplete
                disableClearable={true}
                autoSelect={false}
                minWidth={"300px"}
                addMargin={true}
                title={"Drugs Involved"}
                groupBy={option => option.drugClassification.name}
                options={drugTypes.sort((a, b) =>
                  a.drugClassification.position < b.drugClassification.position
                    ? -1
                    : 1,
                )}
                loading={false}
                error={""}
                multiple={true}
                limitTags={3}
                value={
                  incident?.drugTypes
                    ? drugTypes.filter(option =>
                        incident.drugTypes.some(dt => option.id === dt.id),
                      )
                    : []
                }
                name={"name"}
                stateName={"selectedDrugTypes"}
                updateFields={handleReviewFixUpdate}
                id={"DrugsInvolvedObjects"}
                userRoles={user?.roleNames}
                setPausePolling={setPausePolling}
                {...props}
              />
            </div>
          )}

          {/* Call Type section */}
          {sections?.classification && (
            <div style={{ padding: "0 16px 16px 16px" }}>
              <CustomAutoComplete
                disableClearable={true}
                autoSelect={false}
                title={"Call Type"}
                options={classifications}
                addMargin={true}
                stateName={"classification"}
                value={incident?.classification ? incident.classification : null}
                updateFields={handleReviewFixUpdate}
                multiple={false}
                nameFormatter={false}
                minWidth={"300px"}
                userRoles={user?.roleNames}
                {...props}
                setPausePolling={setPausePolling}
                id={"classificationAutocomplete"}
              />
            </div>
          )}

          {/* Race Data section */}
          {sections?.race && (
            <div style={{ padding: "0 16px 16px 16px" }}>
              <div style={{ marginBottom: 8 }}>
                Race data and/or Use of Force info is incomplete.
              </div>
              <Button
                type="button"
                className={clsx(classes?.buttonHover, classes?.navBarButtons)}
                style={{ width: "fit-content" }}
                variant="contained"
                onClick={() => setRaceModalOpen(true)}
              >
                Open Race Data Editor
              </Button>

              {/* Nested editor (re-using your existing component) */}
              <RaceDataModal
                investigatingOfficerId={
                  incident?.incidentReview?.investigatingOfficer?.id
                }
                raceModal={raceModalOpen}
                setRaceModal={setRaceModalOpen}
                classes={classes}
                nightMode={nightMode}
                reviewStatuses={reviewStatuses}
                handleReadyForApproval={handleReadyForApproval}
                ticket={raceTicketToEdit}
                incidentPeople={props?.incidentPeople}
                userRoles={props?.userRoles}
                incidentIndex={props?.incidentIndex}
                incident={incident}
                user={user}
                {...props}
              />
            </div>
          )}

          {/* Incident Flags section */}
          {(sections?.axon || sections?.incidentFlags) && (
            <div style={{ padding: "0 16px 16px 16px" }}>
              <CustomAutoComplete
                disableClearable={true}
                autoSelect={false}
                title={"Incident\xa0Flags"}
                options={incidentFlags?.sort((a, b) => {
                  if (a.position < b.position) {
                    return -1;
                  } else {
                    return 1;
                  }
                })}
                addMargin={true}
                stateName={"incidentFlags"}
                value={
                  incident?.incidentFlags
                    ? incidentFlags?.filter(option =>
                        incident.incidentFlags?.some(
                          flag => option.id === flag.id,
                        ),
                      )
                    : []
                }
                updateFields={handleReviewFixUpdate}
                multiple={true}
                nameFormatter={false}
                minWidth={"150px"}
                userRoles={user?.roleNames}
                {...props}
                limitTags={3}
                setPausePolling={setPausePolling}
                id={"incidentFlagsAutocomplete"}
              />
            </div>
          )}
          {sections?.pold && (
            <div>
              <p>
                If a liquor licensed establishment contributed to this incident,
                select Yes then include the business or location.
              </p>
              <PoldFields
                incident={incident}
                poldOptOut={incident?.ori?.poldOptOut}
                poldInstance={incident?.poldInstance}
                setPausePolling={setPausePolling}
                user={user}
                {...props}
              />
            </div>
          )}
        </div>
        <div
          style={{
            padding: 16,
            display: "flex",
            gap: 12,
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="contained"
            onClick={onClose}
            className={clsx(classes?.buttonHover, classes?.navBarButtons)}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={revalidateCallback}
            className={clsx(classes?.buttonHover, classes?.navBarButtons)}
          >
            Revalidate
          </Button>
        </div>
      </div>
    </Modal>
  );
}
